import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { AthleteDatasourcesQuery } from 'api/integrations/storyblok/generated/cms'
import { cn } from 'lib/utils'
import { useRouter } from 'next/router'
import { AthletesFilter } from 'constants/common/filters'
import DatePicker from 'components/common/DatePicker'
import { CloseIcon, XIcon } from 'components/icons'
import { Button } from 'components/ui/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from 'components/ui/form'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select'
import { FilterAthletesSchema, FilterAthletesSchemaType } from '../FilterAthleteSchema'
import { getIsQueryParamChanged, removeGenderPrefix } from '../utils'

interface Props {
  athleteDatasources: AthleteDatasourcesQuery
}

export const FresnoAthleteFilter = ({ athleteDatasources }: Props) => {
  const router = useRouter()

  const { sport, position, sortBy, from, to } = router.query

  const sportFilter = (Array.isArray(sport) ? sport[0] : sport) || ''
  const positionFilter = (Array.isArray(position) ? position[0] : position) || ''
  const sortByFilter = (Array.isArray(sortBy) ? sortBy[0] : sortBy) || ''
  const toFilter = (Array.isArray(to) ? to[0] : to) || undefined
  const fromFilter = (Array.isArray(from) ? from[0] : from) || undefined

  const formMethods = useForm<FilterAthletesSchemaType>({
    resolver: zodResolver(FilterAthletesSchema),
    defaultValues: {
      sport: sportFilter,
      position: positionFilter,
      sortBy: sortByFilter,
      availability: {
        from: fromFilter ? new Date(fromFilter) : undefined,
        to: toFilter ? new Date(toFilter) : undefined,
      },
    },
    mode: 'onChange',
  })
  const isClearVisible = Object.keys(formMethods.getValues()).some((key) => !!key)

  const formValues = formMethods.watch()

  // setting query params on form change
  useEffect(() => {
    const { isQueryParamChanged, newQueryParams } = getIsQueryParamChanged({
      sport: formValues.sport,
      position: formValues.position,
      collegeName: '',
      sortBy: formValues.sortBy,
      from:
        formValues.availability?.from === undefined ? '' : String(formValues.availability?.from),
      to: formValues.availability?.to === undefined ? '' : String(formValues.availability?.to),
    })

    if (isQueryParamChanged) {
      void router.push({ pathname: router.pathname, query: newQueryParams.toString() }, undefined, {
        shallow: true,
      })
    }
  }, [formValues, router])

  const isPositionSelectDisabled =
    athleteDatasources.positions?.items.filter((positionItem) =>
      positionItem.value.startsWith(removeGenderPrefix(formValues.sport)),
    ).length === 0

  const sportWatch = formMethods.watch('sport')
  const positionWatch = formMethods.watch('position')
  const sortByWatch = formMethods.watch('sortBy')
  const availabilityWatch = formMethods.watch('availability')

  return (
    <div className="relative px-8 sm:px-12 md:px-16 lg:px-24 py-8 sm:py-12 bg-slate-900 w-full overflow-hidden flex flex-col items-center justify-center box-border text-center text-5xl text-white">
      <div className="self-stretch flex flex-col items-center justify-start gap-[28px]">
        <div className="self-stretch flex flex-col items-center justify-center">
          <div className="flex-1 relative leading-[150%] font-semibold">
            <h1 className="inline-block">FRESNO STATE</h1>
          </div>
          <div className="flex-1 relative text-xl leading-[150%] font-semibold">
            <h2 className="inline-block">FRESNO STATE ATHLETES</h2>
          </div>
        </div>

        <div className="w-full flex flex-col sm:flex-row items-start justify-start gap-[16px] max-w-[1250px] text-left text-sm text-slate-300">
          <Form {...formMethods}>
            <form className="w-full">
              <div className="lg:flex lg:flex-row lg:justify-start grid grid-cols-2 gap-4 w-full">
                <FormField
                  required
                  name="sport"
                  control={formMethods.control}
                  render={({ field }) => (
                    <FormItem className="w-full xl:max-w-[250px] lg:max-w-[215px]">
                      <FormLabel className="text-sm leading-[150%] font-medium text-slate-300">
                        Sport
                      </FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl
                          className={cn(
                            'w-full bg-slate-900 border-[1px] border-solid border-slate-700 text-xs text-slate-400',
                            field.value && 'text-brand-mint font-semibold',
                          )}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select sport" />
                          </SelectTrigger>
                        </FormControl>
                        <FormMessage />
                        <SelectContent>
                          {athleteDatasources.sports?.items
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((sportItem) => (
                              <SelectItem key={sportItem.value} value={sportItem.value}>
                                {sportItem.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      {sportWatch && (
                        <button
                          type="button"
                          className="flex items-center gap-1 text-slate-400"
                          onClick={(e) => {
                            e.preventDefault()
                            formMethods.setValue('sport', '')
                          }}
                        >
                          <CloseIcon className="h-4 w-4" />
                          <span>Clear</span>
                        </button>
                      )}
                    </FormItem>
                  )}
                />
                <FormField
                  disabled={isPositionSelectDisabled}
                  required
                  name="position"
                  control={formMethods.control}
                  render={({ field }) => (
                    <FormItem className="w-full xl:max-w-[250px] lg:max-w-[215px]">
                      <FormLabel className="text-sm leading-[150%] font-medium text-slate-300">
                        Position
                      </FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                        disabled={isPositionSelectDisabled}
                      >
                        <FormControl
                          className={cn(
                            'w-full bg-slate-900 border-[1px] border-solid border-slate-700 text-xs text-slate-400',
                            field.value && 'text-brand-mint font-semibold',
                          )}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select position" />
                          </SelectTrigger>
                        </FormControl>
                        <FormMessage />
                        <SelectContent>
                          {athleteDatasources.positions?.items
                            .filter((positionItem) =>
                              positionItem.value.startsWith(removeGenderPrefix(formValues.sport)),
                            )
                            .map((positionItem) => (
                              <SelectItem key={positionItem.value} value={positionItem.value}>
                                {positionItem.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      {positionWatch && (
                        <button
                          type="button"
                          className="flex items-center gap-1 text-slate-400"
                          onClick={(e) => {
                            e.preventDefault()
                            formMethods.setValue('position', '')
                          }}
                        >
                          <CloseIcon className="h-4 w-4" />
                          <span>Clear</span>
                        </button>
                      )}
                    </FormItem>
                  )}
                />

                <div className="flex flex-col gap-2">
                  <FormField
                    control={formMethods.control}
                    name="availability"
                    render={({ field }) => (
                      <DatePicker
                        dark
                        label="Availability"
                        size="lg"
                        placeholder="Availability"
                        captionLayout="buttons"
                        mode="range"
                        selected={availabilityWatch}
                        onSelect={field.onChange}
                        // disabled={(date) => date > new Date() || date < new Date('1900-01-01')}
                        initialFocus
                      />
                    )}
                  />
                  {availabilityWatch?.from && availabilityWatch.to && (
                    <button
                      type="button"
                      className="flex items-center gap-1 text-slate-400"
                      onClick={(e) => {
                        e.preventDefault()
                        formMethods.setValue('availability', undefined)
                      }}
                    >
                      <CloseIcon className="h-4 w-4" />
                      <span>Clear</span>
                    </button>
                  )}
                </div>

                <FormField
                  required
                  name="sortBy"
                  control={formMethods.control}
                  render={({ field }) => (
                    <FormItem className="w-full lg:ml-auto lg:max-w-[172px]">
                      <FormLabel className="text-sm leading-[150%] font-medium text-slate-300">
                        Sort By
                      </FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl
                          className={cn(
                            'w-full bg-slate-900 border-[1px] border-solid border-slate-700 text-xs text-slate-400',
                            field.value && 'text-brand-mint font-semibold',
                          )}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Sort by" />
                          </SelectTrigger>
                        </FormControl>
                        <FormMessage />
                        <SelectContent>
                          <SelectItem value={AthletesFilter.PriceAsc}>Price - Ascending</SelectItem>
                          <SelectItem value={AthletesFilter.PriceDesc}>
                            Price - Descending
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      {sortByWatch && (
                        <button
                          type="button"
                          className="flex items-center gap-1 text-slate-400"
                          onClick={(e) => {
                            e.preventDefault()
                            formMethods.setValue('sortBy', '')
                          }}
                        >
                          <CloseIcon className="h-4 w-4" />
                          <span>Clear</span>
                        </button>
                      )}
                    </FormItem>
                  )}
                />
              </div>
              <Button
                variant="ghost"
                colorType="slate"
                leftIcon={<XIcon className="w-[18px] h-[18px]" />}
                className={cn(
                  'w-min h-[37px] mt-5 ml-auto text-sm pl-0',
                  !isClearVisible && 'hidden',
                )}
                onClick={(e) => {
                  e.preventDefault()
                  formMethods.reset({
                    position: '',
                    sortBy: '',
                    sport: '',
                    collegeName: '',
                    availability: { from: undefined, to: undefined },
                  })
                }}
              >
                <div className="leading-[150%] font-medium">Reset filter</div>
              </Button>
            </form>
          </Form>
        </div>
      </div>
    </div>
  )
}
